import { useCallback, useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import nanoid from '@utils/random'

const MAX_SNACKBARS = 5

export type SnackbarVariant = 'success' | 'error'

export interface SnackbarItem {
  id: string
  message: string
  variant: SnackbarVariant
  autoHide?: boolean
}

type UseSnackbar = {
  snackbars: SnackbarItem[]
  showSnackbar: (message: string, variant: SnackbarItem['variant']) => void
  hideSnackbar: (id: string) => void
}

const useSnackbar = (): UseSnackbar => {
  const queryClient = useQueryClient()
  const queryCache = queryClient.getQueryCache()

  const [snackbars, setSnackbars] = useState<SnackbarItem[]>([])

  const unsubscribeFnRef = useRef<() => void>(undefined)

  const updateSnackbars = useCallback(
    (args: any) => {
      if (
        args?.action?.type === 'invalidate' &&
        args.query.queryKey[0] === 'snackbars'
      ) {
        setSnackbars(
          queryClient.getQueryData<SnackbarItem[]>(['snackbars']) ?? []
        )
      }
    },
    [queryClient]
  )

  useEffect(() => {
    setSnackbars(queryClient.getQueryData<SnackbarItem[]>(['snackbars']) ?? [])
    unsubscribeFnRef.current = queryCache.subscribe(updateSnackbars)

    return () => {
      if (unsubscribeFnRef.current) {
        unsubscribeFnRef.current()
      }
    }
  }, [updateSnackbars, queryCache, queryClient])

  const showSnackbar = useCallback(
    (
      message: SnackbarItem['message'],
      variant: SnackbarItem['variant'],
      options?: Omit<SnackbarItem, 'id' | 'message' | 'variant'>
    ) => {
      const newSnackbar: SnackbarItem = {
        id: nanoid(),
        message,
        variant,
        autoHide: options?.autoHide ?? true,
        ...options,
      }

      queryClient.setQueryData(['snackbars'], (prev: SnackbarItem[]) =>
        [newSnackbar, ...(prev ?? [])].slice(0, MAX_SNACKBARS)
      )
      queryClient.invalidateQueries({
        queryKey: ['snackbars'],
        exact: true,
      })
    },
    [queryClient]
  )

  const hideSnackbar = useCallback(
    (id: string) => {
      queryClient.setQueryData(['snackbars'], (prev: SnackbarItem[]) =>
        prev.filter((snackbar) => snackbar.id !== id)
      )
      queryClient.invalidateQueries({
        queryKey: ['snackbars'],
        exact: true,
      })
    },
    [queryClient]
  )

  return {
    snackbars,
    showSnackbar,
    hideSnackbar,
  }
}

export default useSnackbar
